import type { Maybe } from '@vrplatform/utils';
import {
  Box,
  type CardProps,
  Center,
  Group,
  LoadingOverlay,
  Card as MCard,
  Text,
} from '@mantine/core';
import type { ReactNode } from 'react';
import { useLegacyColors } from '@vrplatform/theme';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { PercentageBadge } from '@vrplatform/components';

type ContainerProps = {
  children: ReactNode;
  loading: boolean;
  isFetching?: boolean;
  error: Maybe<Error | { message: string }>;
};

const minHeight = 350;

export const ChartCard = ({
  children,
  error,
  loading,
  isFetching,

  ...cardProps
}: ContainerProps & CardProps) => {
  if (loading || error)
    return (
      <BaseCard {...cardProps}>
        <Center h="100%">
          {loading ? (
            <LoadingIndicator size="sm" />
          ) : (
            <Text>{error?.message}</Text>
          )}
        </Center>
      </BaseCard>
    );

  return (
    <BaseCard {...cardProps} pos="relative">
      {children}
      <LoadingOverlay visible={isFetching} />
    </BaseCard>
  );
};

export const getPercentageDifference = (a: number, b: number) => {
  if (!a || !b) return null;

  return ((a - b) / b) * 100;
};

export const ChartCardTitle = ({
  title,
  previousTotal,
  total,
  diffPercentage,
  inverseBadgeColors,
}: {
  title: string;
  total?: number | string;
  previousTotal?: number | string;
  diffPercentage?: number | null;
  inverseBadgeColors?: boolean;
}) => {
  const { themeColors } = useLegacyColors();

  return (
    <Box>
      <Text
        component="h3"
        m={0}
        mb={5}
        size="md"
        fw={400}
        c={themeColors.neutral[6]}
      >
        {title}
      </Text>
      <Group gap="xs">
        <Text component="p" size="1.7rem" m={0} mb={5} fw={500}>
          {total}
        </Text>
        <PercentageBadge
          value={diffPercentage}
          inverseColors={inverseBadgeColors}
        />
      </Group>
      <Text component="p" size="sm" m={0} mb="sm" color="#737373">
        vs. {previousTotal} previous period
      </Text>
    </Box>
  );
};

const BaseCard = ({
  children,
  ...cardProps
}: { children: ReactNode } & CardProps) => (
  <MCard
    withBorder
    radius="lg"
    display="flex"
    sx={{ flexDirection: 'column', minHeight, height: '100%' }}
    {...cardProps}
  >
    {children}
  </MCard>
);
